import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { NAV_CATEGORIES, serviceFormHref } from '../mock';
import { trackEvent } from '../lib/customerEventTracker';
import TrustStrip from './TrustStrip';

const HERO_QUICK_CATEGORIES = [
  { category: 'GST', serviceCode: 'GST_REGISTRATION', labelKey: 'hero.quick_gst_registration', labelFallback: 'GST Registration' },
  { category: 'GST', serviceCode: 'GST_FILING', labelKey: 'hero.quick_gst_filing', labelFallback: 'GST Filings' },
  { category: 'COMPANY' },
  { category: 'MCA', serviceCode: 'ROC_ANNUAL', labelKey: 'hero.quick_mca_roc', labelFallback: 'MCA / ROC' },
  { category: 'LICENSE', serviceCode: 'FSSAI_LICENSE', labelKey: 'hero.quick_fssai', labelFallback: 'FSSAI' },
  { category: 'TRADEMARK' },
  { category: 'LICENSE', serviceCode: 'MSME_REG', labelKey: 'hero.quick_msme', labelFallback: 'MSME / Udyam' },
];

const Hero = () => {
  const { t } = useLanguage();
  const catLabel = (cat) => t(`category.${cat.category}`, cat.label);
  const quickCats = HERO_QUICK_CATEGORIES
    .map((entry) => {
      const cat = NAV_CATEGORIES.find((c) => c.category === entry.category);
      if (!cat) return null;
      return { ...entry, cat };
    })
    .filter(Boolean);

  return (
    <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 hero-gradient-sage overflow-hidden">
      <div className="relative max-w-4xl mx-auto px-4 md:px-8 text-center animate-fade-in-up">
        <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-normal leading-[1.15] text-[#0f2540] mb-6 text-balance">
          {t('hero.t1', "India's Trusted")}{' '}
          <span className="text-[#14A098]">{t('hero.t2', 'CA-Led')}</span>
          <br />
          {t('hero.t3', 'Tax & Compliance Platform')}
        </h1>
        <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
          {t('hero.subtitle', "Whether you're salaried, self-employed or running a business, a dedicated SolveTax CA takes care of your Income Tax, GST and compliance — accurately and on time.")}
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3 mb-7">
          <Link to="/get-started">
            <button
              type="button"
              onClick={() =>
                trackEvent({
                  event_name: 'cta_click',
                  event_action: 'click',
                  event_status: 'success',
                  event_category: 'navigation',
                  cta_name: 'hero_get_started',
                  page_path: '/home',
                })
              }
              className="inline-flex items-center gap-2 rounded-full bg-[#0f2540] hover:bg-[#0a1c33] text-white px-8 h-12 font-semibold shadow-lg shadow-[#0f2540]/20 transition-colors"
            >
              {t('hero.cta_get_started', 'Get Started')} <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
          <a
            href="tel:+919346004430"
            onClick={() =>
              trackEvent({
                event_name: 'cta_click',
                event_action: 'click',
                event_status: 'success',
                event_category: 'navigation',
                cta_name: 'hero_talk_to_expert',
                page_path: '/home',
              })
            }
          >
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-full border-2 border-[#14A098] text-[#14A098] hover:bg-[#14A098] hover:text-white px-8 h-12 font-semibold transition-colors bg-white"
            >
              <Phone className="w-4 h-4" /> {t('hero.cta_talk', 'Talk to an Expert')}
            </button>
          </a>
        </div>

        <TrustStrip />
      </div>

      <div className="relative max-w-5xl mx-auto px-4 md:px-8 mt-14 md:mt-16">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3 md:gap-4">
          {quickCats.map(({ cat, serviceCode, labelKey, labelFallback }) => {
            const svc = serviceCode
              ? cat.services.find((s) => s.code === serviceCode)
              : cat.services[0];
            const FirstIcon = svc?.icon;
            const label = labelKey ? t(labelKey, labelFallback) : catLabel(cat);
            return (
              <Link
                key={cat.category + (serviceCode || '')}
                to={svc ? serviceFormHref(svc.code) : '/all-services'}
                className="card-lift group flex flex-col items-center text-center gap-2 rounded-2xl border border-white bg-white/80 backdrop-blur-sm px-3 py-5 shadow-sm hover:shadow-md hover:border-[#14A098]/30 transition-colors"
              >
                <span className="w-10 h-10 rounded-xl bg-teal-50 group-hover:bg-[#14A098] flex items-center justify-center transition-colors">
                  {FirstIcon && <FirstIcon className="w-5 h-5 text-[#14A098] group-hover:text-white transition-colors" />}
                </span>
                <span className="text-sm font-bold text-[#0f2540]">{label}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Hero;
